
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Auth as SupabaseAuth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/landing/Header";

const THEME_CONFIG = {
  theme: ThemeSupa,
  variables: {
    default: {
      colors: {
        brand: "#F97316",
        brandAccent: "#EA580C",
        inputBackground: "white"
      },
      borderWidths: {
        buttonBorderWidth: "1px",
      },
      fonts: {
        bodyFontFamily: "Inter",
        buttonFontFamily: "Inter",
        inputFontFamily: "Inter",
        labelFontFamily: "Inter"
      },
      radii: {
        borderRadiusButton: "8px",
        inputBorderRadius: "8px",
      },
    },
    dark: {
      colors: {
        brandAccent: "#EA580C",
        inputBackground: "#292524",
        inputText: "white",
        inputPlaceholder: "#A8A29E",
        inputBorder: "#44403C",
        inputLabelText: "white",
        messageText: "white"
      }
    }
  },
  style: {
    button: {
      boxShadow: "inset 0px 1px 0px 0px #FFFFFF66",
      fontWeight: 600
    },
  },
} as const;

const LOCALIZATION = {
  variables: {
    sign_in: {
      email_label: "Email",
      password_label: "Password",
      button_label: "Log in",
      loading_button_label: "Logging in",
      social_provider_text: "Log in with {{provider}}",
      link_text: "Already have an account? Log in"
    },
    sign_up: {
      email_label: "Email",
      password_label: "Password",
      button_label: "Get started",
      loading_button_label: "Signing up",
      social_provider_text: "Sign up with {{provider}}",
      link_text: "Don't have an account? Sign up"
    },
  },
} as const;

const AuthContent = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');

      if (code) {
        try {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;

          if (data.session) {
            // Check if this is a recovery (password reset) session
            const token = data.session.access_token;
            const tokenData = JSON.parse(atob(token.split('.')[1]));
            const isRecovery = tokenData.amr?.some(
              (method: { method: string }) => method.method === 'recovery'
            );

            if (isRecovery) {
              navigate('/auth/reset-password', { replace: true });
            } else {
              navigate('/dashboard', { replace: true });
            }
          }
        } catch (error) {
          console.error('Error handling auth callback:', error);
          navigate('/auth', { replace: true });
        }
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="w-full max-w-md">
      <div className="mb-8">
        <h1 className="mb-3 text-center text-3xl font-semibold text-stone-900 dark:text-white">
          Welcome to IELTS Writing Practice
        </h1>
        <p className="text-center text-base text-stone-500 dark:text-stone-500">
          Sign in or create an account to start practicing
        </p>
      </div>

      <SupabaseAuth
        supabaseClient={supabase}
        appearance={THEME_CONFIG}
        localization={LOCALIZATION}
        providers={["google"]}
        redirectTo={`${window.location.origin}/auth`}
        theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
      />
    </div>
  );
};

const Auth = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;

    const checkSession = async () => {
      if (user) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Only redirect to dashboard if there's no code in the URL
          // This prevents immediate redirect during password reset flow
          const params = new URLSearchParams(window.location.search);
          const code = params.get('code');

          if (!code) {
            navigate("/dashboard", { replace: true });
          }
        }
      }
    };

    checkSession();
  }, [user, loading, navigate]);

  if (loading) return null;

  return (
    <>
      <Header />
      <main
        className="flex min-h-full items-center justify-center bg-stone-50 dark:bg-stone-950 px-4 py-20"
        aria-labelledby="auth-title"
      >
        <AuthContent />
      </main>
    </>
  );
};

export default Auth;
